import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle } from "@material-ui/core";
import { useSelector } from "react-redux";
import RewardTypeSelector from "../component/RewardTypeSelector";
import DailyTaskRewardItem from "../component/DailyTaskRewardItem";
import { Toast } from "../util/Toast";
import { permissionError } from "../util/Alert";

const RewardList = ({ title, rewards, onChange }) => {
  const [rewardType, setRewardType] = useState("diamond");
  const [diamonds, setDiamonds] = useState(1);
  const [rcoins, setRcoins] = useState(1);
  const [giftQuantity, setGiftQuantity] = useState(1);
  const [validity, setValidity] = useState(1);
  const [validityType, setValidityType] = useState("day");
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [selectedSvga, setSelectedSvga] = useState(null);
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [selectedGift, setSelectedGift] = useState(null);
  const [selectedGiftCategory, setSelectedGiftCategory] = useState("");
  const [error, setError] = useState("");

  const add = () => {
    const item = { frame: selectedFrame, svga: selectedSvga, background: selectedBackground, gift: selectedGift }[rewardType];
    const quantity = Number(rewardType === "diamond" ? diamonds : rewardType === "rcoin" ? rcoins : rewardType === "gift" ? giftQuantity : 1);
    if (!Number.isSafeInteger(quantity) || quantity < 1) return setError("Quantity must be a positive whole number.");
    if (!["diamond", "rcoin"].includes(rewardType) && !item) return setError("Select an item first.");
    const reward = { type: rewardType, quantity };
    if (item) {
      reward.itemId = item.value;
      reward.item = { name: item.label, image: item.image };
    }
    if (["frame", "svga", "background"].includes(rewardType)) {
      if (!Number.isSafeInteger(Number(validity)) || Number(validity) < 1) return setError("Validity must be a positive whole number.");
      reward.validity = Number(validity);
      reward.validityType = validityType;
    }
    onChange([...rewards, reward]);
    setError("");
  };

  return (
    <div className="border rounded p-3 mb-3">
      <h5 className="text-danger">{title}</h5>
      <div className="d-flex flex-wrap gap-3 mb-3">
        {rewards.map((reward, index) => (
          <div key={`${reward.type}-${index}`} className="border rounded p-2 text-center">
            <DailyTaskRewardItem reward={reward} />
            <button type="button" className="btn btn-outline-danger btn-sm mt-2"
              onClick={() => onChange(rewards.filter((_, i) => i !== index))}>Remove</button>
          </div>
        ))}
      </div>
      <RewardTypeSelector rewardType={rewardType} setRewardType={setRewardType}
        validity={validity} setValidity={setValidity} validityType={validityType} setValidityType={setValidityType}
        diamonds={diamonds} setDiamonds={setDiamonds} rcoins={rcoins} setRcoins={setRcoins} showRcoin
        giftQuantity={giftQuantity} setGiftQuantity={setGiftQuantity}
        selectedFrame={selectedFrame} setSelectedFrame={setSelectedFrame}
        selectedSvga={selectedSvga} setSelectedSvga={setSelectedSvga}
        selectedBackground={selectedBackground} setSelectedBackground={setSelectedBackground}
        selectedGift={selectedGift} setSelectedGift={setSelectedGift}
        selectedGiftCategory={selectedGiftCategory} setSelectedGiftCategory={setSelectedGiftCategory} />
      {error && <div className="text-danger">{error}</div>}
      <button type="button" className="btn btn-outline-danger btn-sm" onClick={add}>Add {title} reward</button>
    </div>
  );
};

const GiftBoxLevels = () => {
  const hasPermission = useSelector((state) => state.admin.admin.flag);
  const [levels, setLevels] = useState([]);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get("giftBox/admin/levels")
      .then(({ data }) => {
        if (data.status) setLevels(data.data.levels || []);
        else Toast("error", data.message);
      })
      .catch((err) => Toast("error", err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, []);

  const edit = (level) => setDraft({
    level: level.level,
    requiredPoints: level.requiredPoints,
    freeRewards: [...level.freeRewards],
    premiumRewards: [...level.premiumRewards],
  });

  const saveDraft = () => {
    const points = Number(draft.requiredPoints);
    const previous = levels[draft.level - 2];
    const next = levels[draft.level];
    if (!Number.isSafeInteger(points) || points < 0 ||
      (previous && points <= previous.requiredPoints) || (next && points >= next.requiredPoints)) {
      return setError("Points must be nonnegative whole numbers and increase with each level.");
    }
    const updated = { ...draft, requiredPoints: points };
    setLevels((current) => current.some((item) => item.level === updated.level)
      ? current.map((item) => item.level === updated.level ? updated : item)
      : [...current, updated]);
    setDraft(null);
    setError("");
  };

  const saveAll = async () => {
    if (!hasPermission) return permissionError();
    setSaving(true);
    try {
      const { data } = await axios.put("giftBox/admin/levels", {
        levels: levels.map(({ level, requiredPoints, freeRewards, premiumRewards }) => ({
          level, requiredPoints, freeRewards, premiumRewards,
        })),
      });
      if (!data.status) throw new Error(data.message);
      setLevels(data.data.levels);
      Toast("success", "Gift Box levels saved");
    } catch (err) {
      Toast("error", err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="card"><div className="card-body">
        {loading ? <p>Loading...</p> : <>
          <table className="table table-striped">
            <thead><tr><th>Level</th><th>Total points required</th><th>Free Gift</th><th>Premium Gift</th><th>Action</th></tr></thead>
            <tbody>{levels.map((level) => <tr key={level.level}>
              <td>{level.level}</td><td>{level.requiredPoints}</td>
              <td>{level.freeRewards.length} reward(s)</td><td>{level.premiumRewards.length} reward(s)</td>
              <td><button type="button" className="btn btn-outline-danger btn-sm" onClick={() => edit(level)}>Edit</button></td>
            </tr>)}</tbody>
          </table>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-outline-danger" onClick={() => edit({
              level: levels.length + 1,
              requiredPoints: levels.length ? levels[levels.length - 1].requiredPoints + 1 : 1,
              freeRewards: [], premiumRewards: [],
            })}>Add Level</button>
            {levels.length > 1 && <button type="button" className="btn btn-outline-secondary"
              onClick={() => setLevels(levels.slice(0, -1))}>Remove Last Level</button>}
            <button type="button" className="btn btn-danger" disabled={saving || !levels.length} onClick={saveAll}>
              {saving ? "Saving..." : "Save All Levels"}
            </button>
          </div>
        </>}
      </div></div>
      {draft && <Dialog open fullWidth maxWidth="md" onClose={() => setDraft(null)}
        PaperProps={{ style: { backgroundColor: "#161622", color: "#fff" } }}>
        <DialogTitle disableTypography style={{ color: "#fff", fontSize: "1.25rem", fontWeight: 500, lineHeight: 1.6 }}>
          Configure Gift Box Level {draft.level}
        </DialogTitle>
        <DialogContent>
          <label className="form-label" htmlFor="required-points">Total monthly points required</label>
          <input id="required-points" className="form-control mb-3" type="number" min="0" step="1"
            value={draft.requiredPoints}
            onChange={(event) => setDraft({ ...draft, requiredPoints: event.target.value })} />
          <RewardList title="Free Gift" rewards={draft.freeRewards}
            onChange={(freeRewards) => setDraft({ ...draft, freeRewards })} />
          <RewardList title="Premium Gift" rewards={draft.premiumRewards}
            onChange={(premiumRewards) => setDraft({ ...draft, premiumRewards })} />
          {error && <div className="text-danger mb-3">{error}</div>}
          <div className="d-flex justify-content-end gap-2 mb-3">
            <button type="button" className="btn btn-secondary" onClick={() => setDraft(null)}>Cancel</button>
            <button type="button" className="btn btn-danger" onClick={saveDraft}>Keep Level Changes</button>
          </div>
        </DialogContent>
      </Dialog>}
    </>
  );
};

export default GiftBoxLevels;
